/**
 * @name app.infrastructure:EventBus
 * @date 08/01/2014
 * @copyright 2015 Empower Retirement
 * @description
 *
 *      This object provides a mechanism for adding and removing event listeners so that specific functionality can impact
 * 		n subscribers without bubbling to more scopes than would otherwise be necessary.
 *
 * 		The EventBus is added to the globalThis window object so it can be leveraged by objects and libraries not part
 * 		of the same AngularJS application.
 */
import * as amplitude from "@amplitude/analytics-browser";
import _ from "lodash";

import { getEnvironmentDisplayName } from "../../../../react/core/utils/getEnvironmentDisplayName";
import ExternalLogger from "../logger/ExternalLogger";
import URLUtil from "../util/URLUtil";

const EventBus = function ($window, $document) {
    /**
     * Logger.
     */
    const $log = ExternalLogger.getInstance("EventBus");

    /**
     * Reference to the exposed API for this object. Eliminates `this` scoping issues.
     */
    const api = this;

    api.legacyInstance = null;
    api.empowerWebInstance = null;

    /**
     * Internal hash map of listener or callback functions.
     */
    const listeners = {};

    /**
     * Boolean indicating if the event bus should log event details deeply.
     * @type {boolean}
     */
    const _isDeepLoggingEnabled = URLUtil.getURLParam("logEvents") === "true";
    const _isDebugEnabled = URLUtil.getURLParam("debug") === "true";

    //-----------------------------------------------------------------------
    // PUBLIC METHODS
    //-----------------------------------------------------------------------

    /**
     * Adds an event listener or callback method to the event bus. Maintains a hash of listeners based on
     * event type.
     *
     * @param {String} type The string event type linked to an  event listener or callback method. If the string "GLOBAL" is
     * used, the provided callback function will be applied on ANY and ALL dispatched events. Use sparingly.
     * @param {Function} callback The event listener method or callback for a given event type.
     * @param {Object} scope Provides the scope object used when invoking the callback method.
     *
     *
     * !!! dev note !!!
     * to remove
     * @param {Object} aScope (Optional) Provides the ANGULAR scope object used when invoking the callback method.
     *          If aScope is provided, $destroy listeners are added so that added events are removed.
     */
    api.addEventListener = function (type, callback, scope, aScope) {
        let args = [];
        const numOfArgs = arguments.length;

        if (_.isUndefined(type)) {
            throw new Error("event type must be defined");
        }
        if (_.isUndefined(callback)) {
            throw new Error("callback must be defined");
        }

        for (let i = 0; i < numOfArgs; i++) {
            args.push(arguments[i]);
        }

        //User can pass in more than 4 args. If they do, those args will be cached
        //with that listener and applied to the callback function
        args = args.length > 3 ? args.splice(4, args.length - 1) : [];

        const newListener = {
            scope: scope,
            callback: callback,
            args: args
        };

        if (_.isUndefined(listeners[type])) {
            listeners[type] = [];
        }

        listeners[type].push(newListener);

        // !!! dev note !!!
        // we are making EventBus Angular independent and to work with React
        // unsure if param aScope is being passed in anywhere
        // log it so we can further clean up the Angular Scope param

        // this block will be removed when EventBus is tested functional
        if (!_.isUndefined(aScope)) {
            $log.debug(
                "!EventBus - Clean up param aScope!\nEvent type:",
                type,
                "Callback:",
                callback,
                "Scope:",
                scope,
                "Angular Scope:",
                aScope
            );

            // If we have a reference to the angular $scope for the object adding an event listener, add a listener
            // for $destroy so that we can remove event listeners when that happens.

            if (!_.isUndefined(aScope.$on)) {
                aScope.$on("$destroy", function () {
                    $log.debug("Scope DESTROYED - removing event listeners");
                    return api.removeEventListener(type, callback, scope);
                });
            }
        }
    };

    /**
     * Removes an event listener or callback method to the event bus.
     *
     * @param {String} type The string event type linked to an  event listener or callback method.
     * @param {Function} callback The event listener method or callback for a given event type.
     * @param {Object} scope Provides the scope object used when invoking the callback method.
     */
    api.removeEventListener = function (type, callback, scope) {
        if (typeof listeners[type] !== "undefined") {
            const newArray = [];

            //$log.debug("Removing callback for listeners for {0}", [type]);

            // gather all the listeners that are not being deleted into a new array
            // NOTE/TODO: Why not just pluck the single item out of the existing array?
            _.each(listeners[type], function (thisListener) {
                if (thisListener.scope === scope && thisListener.callback === callback) {
                    $log.debug("removing this listener"); // empty log to satisfy jshint
                } else {
                    newArray.push(thisListener);
                }
            });

            // if there are any remaining listeners to add, update the listeners collection, otherwise,
            // remove the item at that key.
            if (newArray.length > 0) {
                listeners[type] = newArray;
            } else {
                delete listeners[type];
            }
        }
    };

    api.stripProperties = function (inData) {
        if (!inData) {
            return inData;
        }

        // choosing lodash due to IE incompatability
        //var data = Object.assign({}, inData);
        const data = _.assign({}, inData);

        //var keys = Object.keys(data);
        const keys = Object.getOwnPropertyNames(data);
        for (let i = 0; i < keys.length; i++) {
            const key = keys[i];

            // choosing lodash due to IE incompatability
            //if (key.startsWith('$')){
            if (_.startsWith(key, "$")) {
                delete data[key];
                // only perform deep logging of the event if it's enabled
                if (_isDeepLoggingEnabled && _isDebugEnabled) {
                    console.log("filteredPropery", key);
                }
            }
        }

        if (_.isArray(inData)) {
            return Object.values(data);
        } else {
            return data;
        }
    };

    api.getEmpowerWebInstance = function () {
        return api.empowerWebInstance;
    };

    /**
     *
     * @param {*} payload
     * event_type: string
     * event_properties: object - optional
     * user_properties: object - optional, properties are passed in during liat-ui, initialize_application_complete_event
     *
     */
    api.dispatchAmplitude = function (payload) {
        // get empowerWebInstance
        const empowerWebInstance = api.getEmpowerWebInstance();

        const currentUrl = $window.location.href;
        payload.event_properties.currentURL = currentUrl;

        //console.log("amplitude payload :: ", payload);
        if (empowerWebInstance) {
            if (payload.user_properties) {
                const updatedUserProperties = api.setAmplitudeUserProperites(
                    payload.user_properties
                );
                payload.user_properties = updatedUserProperties;
            }
            empowerWebInstance.track(payload);
        }
    };
    api.getLegacyInstance = function () {
        return api.legacyInstance;
    };

    /**
     * Dispatches the event type, which essentially invokes any event listener or callback methods associated
     * with the type.
     *
     * Additional optional parameters can be added to the dispatch() method such that they are passed
     * into the callback as parameters. The callback always receives these additional arguments in the order
     * they were passed into the dispatch. Callbacks always have the event object with type and target passed
     * in as the last argument. In the event that no additional params are passed with the event, then it's the
     * only param passed to the callback.
     *
     * @param {String} type The string event type linked to an  event listener or callback method.
     * @param {Object} target The object dispatching the event or source of the event.
     */
    api.dispatch = function (type, inTarget, legacyData = {}) {
        const target = api.stripProperties(inTarget);
        const currentUrl = $window.location.href;
        const event = {
            type: type,
            target: target,
            currentURL: currentUrl
        };

        let args = [];
        const numOfArgs = arguments.length;

        for (let i = 0; i < numOfArgs; i++) {
            const arg = api.stripProperties(arguments[i]);
            args.push(arg);
        }
        /**
         * adds Amplitude event tracking
         * get legacy instance
         */
        const legacyInstance = api.getLegacyInstance();

        const legacyEvent = { type, currentUrl, legacyData };
        //console.log("legacy_ga_event  :: ", legacyEvent);
        if (legacyInstance) {
            if (legacyData.user_properties) {
                const updatedUserProperties = api.setAmplitudeUserProperites(
                    legacyData.user_properties
                );
                const payload = {
                    event_type: "legacy_ga_event",
                    event_properties: legacyEvent,
                    user_properties: updatedUserProperties
                };

                legacyInstance.track(payload);
            } else {
                legacyInstance.track("legacy_ga_event", legacyEvent);
            }
        }

        args = args.length > 2 ? args.splice(2, args.length - 1) : [];
        args = args.length > 0 ? args.concat([event]) : (args = [event]);

        // finds all listeners with matching type and also globalThis listeners, then creates one array of all of them
        // then this becomes the array that is looped over for listeners to dispatch from
        const typeListeners = !_.isUndefined(listeners[type]) ? listeners[type] : [];
        const globalListeners = !_.isUndefined(listeners.GLOBAL) ? listeners.GLOBAL : [];
        const typeAndGlobalListeners = typeListeners.concat(globalListeners);

        if (typeAndGlobalListeners.length <= 0) {
            $log.warn("dispatch( No listeners so not dispatching `{0}` )", [type]);
        } else {
            $log.debug("dispatch( type = {0} )", [type]);
        }

        const logData = args[0] || {};
        logData.type = type;

        // only perform deep logging of the event if it's enabled
        if (_isDeepLoggingEnabled) {
            console.log(logData);
        }

        // NOTE: If there are no event listeners then the dispatch never fires
        _.each(typeAndGlobalListeners, function (thisListener) {
            if (thisListener && thisListener.callback) {
                try {
                    thisListener.callback.apply(thisListener.scope, args);
                } catch (err) {
                    // just log the error such that if one listener fails others are not
                    // impacted
                    $log.error(err);

                    if (err.stack) {
                        $log.error(err.stack);
                    }
                }
            }
        });
    };

    /**
     * Determines if the given object has event listeners.
     *
     * @param type
     * @param callback
     * @param scope
     * @returns {boolean}
     */
    api.hasEventListener = function (type, callback, scope) {
        if (typeof listeners[type] !== "undefined") {
            const numOfCallbacks = listeners[type].length;

            if (callback === undefined && scope === undefined) {
                return numOfCallbacks > 0;
            }
            for (let i = 0; i < numOfCallbacks; i++) {
                const listener = listeners[type][i];
                if ((scope ? listener.scope === scope : true) && listener.callback === callback) {
                    return true;
                }
            }
        }
        return false;
    };

    /**
     * Provides the full list of events currently being listened to. This is mostly used for logging.
     * @returns {string}
     */
    api.getEvents = function () {
        let str = "";

        if (listeners) {
            for (const type in listeners) {
                if (Object.prototype.hasOwnProperty.call(listeners, type)) {
                    const numOfCallbacks = listeners[type].length;

                    for (let i = 0; i < numOfCallbacks; i++) {
                        const listener = listeners[type][i];
                        str +=
                            listener.scope && listener.scope.className
                                ? listener.scope.className
                                : "anonymous";
                        str += " listen for '" + type + "'\n";
                    }
                }
            }
        }
        return str;
    };

    /**
     * Since the dispatch event always provides the event object as the last item in the event handler method, this
     * method provides a convenience to grab the event.
     * @param args
     * @returns {*}
     */
    api.extractEvent = function (args) {
        if (args) {
            return args[args.length - 1];
        }
    };

    /**
     * Adds the event bus to the globalThis window object if it's not already there.
     */
    api.addToGlobal = function () {
        if (!api.existsOnGlobal()) {
            $log.debug(
                "addToGlobal( Adding the eventBus to globalThis object `empower.eventBus` )"
            );
            _.extend($window, {
                empower: {
                    eventBus: api
                }
            });
            if ($document[0]?.createEvent) {
                const event = $document[0].createEvent("Event");
                event.initEvent("eventBusReady", true, true);
                $document[0].dispatchEvent(event);
            } else {
                const event = new CustomEvent("eventBusReady");
                document.dispatchEvent(event, true, true);
            }
        }
    };

    /**
     * Determines if the event bus exists on the globalThis window object.
     * @returns {boolean}
     */
    api.existsOnGlobal = function () {
        return (
            !_.isUndefined($window.empower) &&
            !_.isNull($window.empower) &&
            !_.isUndefined($window.empower.eventBus) &&
            !_.isNull($window.empower.eventBus)
        );
    };

    /**
     * Add any additional common fields from window object to the user properties
     * @param {*} userProperties
     * @returns
     */
    api.setAmplitudeUserProperites = function (userProperties) {
        const env = getEnvironmentDisplayName(window.location.host);

        const appendPayload = userProperties;
        appendPayload.environment = env;
        appendPayload.hostname = window.location.hostname;
        appendPayload.protocol = window.location.protocol;
        appendPayload.accu = window.accu;

        return appendPayload;
    };

    api.createLegacyInstance = function (userId) {
        api.legacyInstance = amplitude.createInstance();

        const amplitudeEnv = getEnvironmentDisplayName(window.location.host);
        // keys for legacy_ga_events
        const legacyApiKey =
            amplitudeEnv === "Production"
                ? "c165f6e95ef99c534bc070242a5aa219" // prod api key
                : "ca5490bfc27fecd4e3d6561e0d151018"; // dev api key
        const serverUrl = "https://webusage.retirementpartner.com/2/httpapi"; // default value

        api.legacyInstance.init(legacyApiKey, userId, {
            serverUrl,
            defaultTracking: false
        });
    };

    /**
     * Create a new instance of Amplitude for Empower Web and Mobile
     */
    api.createEmpowerWebInstance = function (userId) {
        api.empowerWebInstance = amplitude.createInstance();

        const amplitudeEnv = getEnvironmentDisplayName(window.location.host);
        // keys for new amplitude events
        const apiKey =
            amplitudeEnv === "Production"
                ? "46a3bf0f27a13623b0932e31b3ae5fef" // prod api key
                : "b9061098a0f6dd38efa3db6cc647372a"; // dev api key

        const serverUrl = "https://webusage.retirementpartner.com/2/httpapi"; // default value

        api.empowerWebInstance.init(apiKey, userId, {
            serverUrl,
            defaultTracking: {
                attribution: true, // You can opt out of marketing attribution tracking by setting to false
                pageViews: true,
                sessions: true,
                formInteractions: true,
                fileDownloads: true
            }
        });
    };

    // https://www.docs.developers.amplitude.com/data/sdks/browser-2/#tracking-default-events
    // https://amplitude.com/docs/sdks/analytics/browser/browser-sdk-2#track-default-events
    api.amplitudeInit = function ({ userId = "" }) {
        api.createLegacyInstance(userId);

        api.createEmpowerWebInstance(userId);
    };

    //-----------------------------------------------------------------------
    // PUBLIC "API"
    //-----------------------------------------------------------------------

    // return either the new event bus or the one globalThis reference on the window object
    return api.existsOnGlobal() ? $window.empower.eventBus : api;
};

EventBus.$inject = ["$window", "$document"];
export default EventBus;
